import React, { useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { ArrowLeft, Check } from 'lucide-react';

interface QuestionStepProps {
  question: string;
  subtitle: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onNext: () => void;
  onBack: () => void;
  questionNumber: number;
  totalQuestions: number;
}

export function QuestionStep({
  question,
  subtitle,
  placeholder,
  value,
  onChange,
  onNext,
  onBack,
  questionNumber,
  totalQuestions,
}: QuestionStepProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-prosigliere-blue">
          <span className="w-6 h-6 rounded-full bg-prosigliere-blue text-white flex items-center justify-center text-xs">
            {questionNumber}
          </span>
          Question {questionNumber} of {totalQuestions}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight leading-tight">
          {question}
        </h2>
        <p className="text-lg text-muted-foreground">{subtitle}</p>
      </div>

      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={4}
          className="text-xl resize-none"
        />
        {value && (
          <div className="absolute right-3 bottom-3">
            <Check className="w-5 h-5 text-green-600" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-4">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">Press Enter ↵</span>
          <Button onClick={onNext} className="rounded-full px-8">
            {value ? 'Confirm' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
